// @ts-nocheck
// Retry mechanism utility for handling failed operations

/**
 * Retry an async operation with exponential backoff
 * @param {Function} operation - The async operation to retry
 * @param {Object} options - Retry configuration options
 * @returns {Promise} - Promise that resolves with the operation result or rejects after all retries
 */
export const retryOperation = async (operation, options = {}) => {
  const {
    maxRetries = 3,
    baseDelay = 1000, // Base delay in milliseconds
    maxDelay = 10000, // Maximum delay in milliseconds
    backoffFactor = 2, // Exponential backoff multiplier
    retryCondition = (error) => true, // Function to determine if error should trigger retry
    onRetry = (attempt, error) => {}, // Callback for each retry attempt
    onFinalFailure = (error, attempts) => {} // Callback when all retries are exhausted
  } = options;

  let lastError;
  
  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      const result = await operation();
      return result;
    } catch (error) {
      lastError = error;
      
      // If this is the last attempt, don't retry
      if (attempt === maxRetries) {
        onFinalFailure(error, attempt + 1);
        throw error;
      }
      
      // Check if this error should trigger a retry
      if (!retryCondition(error)) {
        throw error;
      }
      
      // Calculate delay with exponential backoff
      const delay = Math.min(
        baseDelay * Math.pow(backoffFactor, attempt),
        maxDelay
      );
      
      // Add some jitter to prevent thundering herd
      const jitteredDelay = delay + Math.random() * 1000;
      
      onRetry(attempt + 1, error);
      
      // Wait before retrying
      await new Promise(resolve => setTimeout(resolve, jitteredDelay));
    }
  }
  
  throw lastError;
};

/**
 * Specific retry configurations for different types of operations
 */
export const retryConfigs = {
  // Network operations (API calls)
  network: {
    maxRetries: 3,
    baseDelay: 1000,
    maxDelay: 8000,
    backoffFactor: 2,
    retryCondition: (error) => {
      // Retry on network errors, timeouts, and 5xx server errors
      if (error.name === 'NetworkError' || error.name === 'TypeError') return true;
      if (error.message?.includes('fetch')) return true;
      if (error.status >= 500 && error.status < 600) return true;
      if (error.status === 408 || error.status === 429) return true; // Timeout or rate limit
      return false;
    }
  },
  
  // Payment operations
  payment: {
    maxRetries: 2,
    baseDelay: 2000,
    maxDelay: 6000,
    backoffFactor: 2,
    retryCondition: (error) => {
      // Only retry on specific payment errors, not validation errors
      if (error.message?.includes('network')) return true;
      if (error.message?.includes('timeout')) return true;
      if (error.status === 503 || error.status === 502) return true; // Service unavailable
      return false;
    }
  },
  
  // File operations (receipt downloads, etc.)
  file: {
    maxRetries: 2,
    baseDelay: 1500,
    maxDelay: 5000,
    backoffFactor: 2,
    retryCondition: (error) => {
      // Retry on network issues but not on file not found
      if (error.status === 404) return false;
      if (error.name === 'NetworkError') return true;
      if (error.status >= 500) return true;
      return false;
    }
  }
};

/**
 * Convenience functions for common retry scenarios
 */

// Retry a network request
export const retryNetworkRequest = (requestFn, customOptions = {}) => {
  return retryOperation(requestFn, {
    ...retryConfigs.network,
    ...customOptions
  });
};

// Retry a payment operation
export const retryPaymentOperation = (paymentFn, customOptions = {}) => {
  return retryOperation(paymentFn, {
    ...retryConfigs.payment,
    ...customOptions
  });
};

// Retry a file operation
export const retryFileOperation = (fileFn, customOptions = {}) => {
  return retryOperation(fileFn, {
    ...retryConfigs.file,
    ...customOptions
  });
};

/**
 * React hook for managing retry state
 * Note: This hook should be imported in React components, not used directly in this file
 */
export const createUseRetry = (React) => (operation, options = {}) => {
  const [isLoading, setIsLoading] = React.useState(false);
  const [error, setError] = React.useState(null);
  const [retryCount, setRetryCount] = React.useState(0);
  const [data, setData] = React.useState(null);

  const execute = React.useCallback(async (...args) => {
    setIsLoading(true);
    setError(null);
    
    try {
      const result = await retryOperation(
        () => operation(...args),
        {
          ...options,
          onRetry: (attempt, error) => {
            setRetryCount(attempt);
            options.onRetry?.(attempt, error);
          },
          onFinalFailure: (error, attempts) => {
            setError(error);
            options.onFinalFailure?.(error, attempts);
          }
        }
      );
      
      setData(result);
      setRetryCount(0);
      return result;
    } catch (err) {
      setError(err);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, [operation, options]);

  const reset = React.useCallback(() => {
    setIsLoading(false);
    setError(null);
    setRetryCount(0);
    setData(null);
  }, []);

  return {
    execute,
    reset,
    isLoading,
    error,
    retryCount,
    data
  };
};

/**
 * Error classification utilities
 */
export const errorTypes = {
  NETWORK: 'network',
  VALIDATION: 'validation',
  PAYMENT: 'payment',
  AUTHENTICATION: 'authentication',
  AUTHORIZATION: 'authorization',
  SERVER: 'server',
  UNKNOWN: 'unknown'
};

export const classifyError = (error) => {
  if (!error) return errorTypes.UNKNOWN;
  
  const message = error.message?.toLowerCase() || '';
  const status = error.status;
  
  // Network errors
  if (error.name === 'NetworkError' || error.name === 'TypeError') {
    return errorTypes.NETWORK;
  }
  if (message.includes('fetch') || message.includes('network')) {
    return errorTypes.NETWORK;
  }
  
  // HTTP status based classification
  if (status) {
    if (status === 401) return errorTypes.AUTHENTICATION;
    if (status === 403) return errorTypes.AUTHORIZATION;
    if (status >= 400 && status < 500) return errorTypes.VALIDATION;
    if (status >= 500) return errorTypes.SERVER;
  }
  
  // Payment specific errors
  if (message.includes('payment') || message.includes('card') || message.includes('billing')) {
    return errorTypes.PAYMENT;
  }
  
  // Validation errors
  if (message.includes('validation') || message.includes('invalid') || message.includes('required')) {
    return errorTypes.VALIDATION;
  }
  
  return errorTypes.UNKNOWN;
};

/**
 * Get user-friendly error messages based on error type
 */
export const getErrorMessage = (error) => {
  const type = classifyError(error);
  
  const messages = {
    [errorTypes.NETWORK]: 'Network connection issue. Please check your internet connection and try again.',
    [errorTypes.VALIDATION]: 'Please check your input and correct any errors.',
    [errorTypes.PAYMENT]: 'Payment processing error. Please verify your payment information and try again.',
    [errorTypes.AUTHENTICATION]: 'Authentication required. Please log in and try again.',
    [errorTypes.AUTHORIZATION]: 'You do not have permission to perform this action.',
    [errorTypes.SERVER]: 'Server error. Please try again in a few moments.',
    [errorTypes.UNKNOWN]: 'An unexpected error occurred. Please try again.'
  };
  
  return messages[type] || messages[errorTypes.UNKNOWN];
};

/**
 * Get suggested actions based on error type
 */
export const getErrorActions = (error) => {
  const type = classifyError(error);
  
  const actions = {
    [errorTypes.NETWORK]: [
      'Check your internet connection',
      'Try refreshing the page',
      'Contact support if the issue persists'
    ],
    [errorTypes.VALIDATION]: [
      'Review and correct the highlighted fields',
      'Ensure all required information is provided',
      'Check for any formatting requirements'
    ],
    [errorTypes.PAYMENT]: [
      'Verify your payment method details',
      'Check if your card has sufficient funds',
      'Try a different payment method',
      'Contact your bank if needed'
    ],
    [errorTypes.AUTHENTICATION]: [
      'Log in to your account',
      'Check if your session has expired',
      'Reset your password if needed'
    ],
    [errorTypes.AUTHORIZATION]: [
      'Contact support for access',
      'Verify your account permissions',
      'Try logging out and back in'
    ],
    [errorTypes.SERVER]: [
      'Wait a few moments and try again',
      'Check our status page for known issues',
      'Contact support if the problem continues'
    ],
    [errorTypes.UNKNOWN]: [
      'Try refreshing the page',
      'Clear your browser cache',
      'Contact support with error details'
    ]
  };
  
  return actions[type] || actions[errorTypes.UNKNOWN];
};